import api from './client'

/**
 * SuggestedAction represents an actionable suggestion from the AI,
 * typically shown when a tool is missing or an operation can't be completed.
 */
export interface SuggestedAction {
  action_type: 'build_tool' | 'suggest_alternative' | 'open_docs' | string
  label: string
  description: string
  prompt: string
  plugin_name?: string
  tool_name?: string
}

/**
 * Request to build a missing tool via Claude Code.
 */
export interface BuildToolRequest {
  action_type: string
  label: string
  description: string
  prompt: string
  plugin_name?: string
  tool_name?: string
  context?: Record<string, unknown>
}

/**
 * Response from the build-tool endpoint.
 */
export interface BuildToolResponse {
  ok: boolean
  message: string
  output?: string
  error?: string
}

/**
 * Plugin information from the API.
 */
export interface PluginInfo {
  name: string
  description: string
  version: string
  enabled: boolean
  tool_count: number
  health_status: string
}

/**
 * Tool information from a plugin.
 */
export interface ToolInfo {
  name: string
  description: string
  danger_level: 'SAFE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  parameters: Array<{
    name: string
    type: string
    description: string
    required: boolean
    default?: unknown
  }>
}

/**
 * Request Claude Code MCP to implement a missing tool.
 *
 * This sends the request to the backend, which forwards it to the
 * Claude Code MCP server running on CT 105.
 */
export async function buildTool(
  action: SuggestedAction | BuildToolRequest
): Promise<BuildToolResponse> {
  const request: BuildToolRequest = {
    action_type: action.action_type,
    label: action.label,
    description: action.description,
    prompt: action.prompt,
    plugin_name: action.plugin_name || '',
    tool_name: action.tool_name || '',
    context: 'context' in action ? action.context : {},
  }

  const response = await api.post<BuildToolResponse>(
    '/plugins/build-tool',
    request
  )
  return response.data
}

/**
 * Get list of all installed plugins.
 */
export async function getPlugins(): Promise<PluginInfo[]> {
  const response = await api.get<PluginInfo[]>('/plugins')
  return response.data
}

/**
 * Get tools for a specific plugin.
 */
export async function getPluginTools(pluginName: string): Promise<ToolInfo[]> {
  const response = await api.get<ToolInfo[]>(`/plugins/${pluginName}/tools`)
  return response.data
}

// ============================================================
// Tool Execution API
// ============================================================

import type {
  ToolCatalogItem,
  ToolExecRequest,
  ToolExecResponse,
  PluginHealth,
  ToolStats,
} from '@/types/plugins'

/**
 * Execute a tool from the UI for testing.
 */
export async function executeToolFromUI(
  toolName: string,
  request: ToolExecRequest
): Promise<ToolExecResponse> {
  const startTime = Date.now()
  try {
    const response = await api.post<ToolExecResponse>(
      `/tools/${encodeURIComponent(toolName)}/exec`,
      request
    )
    return {
      ...response.data,
      durationMs: response.data.durationMs || Date.now() - startTime,
    }
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Execution failed',
      durationMs: Date.now() - startTime,
    }
  }
}

/**
 * Get statistics for a specific tool.
 */
export async function getToolStats(toolName: string): Promise<ToolStats | null> {
  try {
    const [feedbackRes, errorsRes] = await Promise.allSettled([
      api.get(`/feedback/tool/${encodeURIComponent(toolName)}`),
      api.get(`/errors/patterns?tool_name=${encodeURIComponent(toolName)}`),
    ])

    const feedback =
      feedbackRes.status === 'fulfilled' ? feedbackRes.value.data : null
    const errors =
      errorsRes.status === 'fulfilled' ? errorsRes.value.data : null

    if (!feedback?.summary) return null

    const total = (feedback.summary.positive ?? 0) + (feedback.summary.negative ?? 0)
    const satisfaction = total > 0 ? (feedback.summary.positive ?? 0) / total : null

    return {
      totalCalls: feedback.summary.total_feedback ?? 0,
      successRate: 0, // Would need audit_log query
      avgDurationMs: 0, // Would need audit_log query
      satisfaction,
      recentErrors:
        errors?.patterns?.slice(0, 3).map((p: { error_sample: string }) => p.error_sample) ?? [],
      usageByDay: [],
    }
  } catch {
    return null
  }
}

/**
 * Get all tools across all plugins as a flat catalog.
 */
export async function getToolCatalog(): Promise<ToolCatalogItem[]> {
  const plugins = await getPlugins()
  const allTools: ToolCatalogItem[] = []

  // Fetch tools from all plugins in parallel
  const toolPromises = plugins
    .filter((p) => p.enabled)
    .map(async (plugin) => {
      try {
        const tools = await getPluginTools(plugin.name)
        return tools.map((tool) => ({
          name: tool.name,
          description: tool.description,
          plugin: plugin.name,
          dangerLevel: tool.danger_level,
          parameters: tool.parameters.map((p) => ({
            name: p.name,
            type: p.type as ToolCatalogItem['parameters'][0]['type'],
            description: p.description,
            required: p.required,
            default: p.default,
          })),
          satisfaction: null, // Would need feedback API
          totalCalls: 0, // Would need audit_log query
          recentErrors: 0,
        }))
      } catch {
        return []
      }
    })

  const results = await Promise.all(toolPromises)
  results.forEach((tools) => allTools.push(...tools))

  return allTools
}

/**
 * Get plugin health status for all plugins.
 */
export async function getPluginHealthStatus(): Promise<PluginHealth[]> {
  const [plugins, health] = await Promise.all([
    getPlugins(),
    api.get('/health').catch(() => ({ data: { plugins: {} } })),
  ])

  return plugins.map((plugin) => ({
    name: plugin.name,
    version: plugin.version,
    description: plugin.description,
    status: plugin.enabled
      ? health.data.plugins?.[plugin.name]?.ok
        ? 'healthy'
        : 'degraded'
      : 'disabled',
    toolCount: plugin.tool_count,
    avgSatisfaction: null, // Would need feedback aggregation
    totalCalls: 0, // Would need audit_log query
    lastError: health.data.plugins?.[plugin.name]?.error,
  }))
}
